// src/components/runs/RunDetailsModal.jsx
import React from 'react';
import { X, CalendarDays, Clock, User, MapPin, Radio, Inbox } from 'lucide-react';
import { useTheme } from '../../theme/ThemeContext';

const formatRunDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

const formatTime = (timeString) => {
  if (!timeString) return '--:--';
  const [h, m] = timeString.split(':');
  if (!h || !m) return '--:--';
  return `${h}:${m}`;
};

// for SQL/ISO datetime like "2025-11-26 12:50:00" or "2025-11-26T12:50:00.000Z"
const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) {
    return { date: '-', time: '--:--' };
  }

  const d = new Date(dateTimeString);
  if (Number.isNaN(d.getTime())) {
    return { date: '-', time: '--:--' };
  }

  const date = formatRunDate(dateTimeString);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return { date, time: `${hours}:${minutes}` };
};

const statusBadgeClass = (status) => {
  switch (status) {
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'in_progress':
      return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'scheduled':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'cancelled':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const RunDetailsModal = ({ run, loading, onClose }) => {
  const { theme } = useTheme();

  if (!run) return null;

  const primary600 = theme?.primary?.[600] || '#0f172a';
  const secondary600 = theme?.secondary?.[600] || '#4f46e5';

  const items = run.items || []; // from GET /api/runs/:id

  const guardName = (run.guard_first_name || run.guard_last_name)
    ? `${run.guard_first_name || ''} ${run.guard_last_name || ''}`.trim()
    : `Guard #${run.guard_id}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={() => !loading && onClose()}
      />

      {/* Card */}
      <div className="relative z-10 w-full max-w-4xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white text-sm font-semibold">
              {guardName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-300">
                Run Details
              </p>
              <h2 className="text-sm sm:text-base font-semibold text-white">
                {guardName}{' '}
                {run.guard_code && (
                  <span className="text-slate-300 text-xs font-normal">
                    • {run.guard_code}
                  </span>
                )}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span
              className={`hidden sm:inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-medium ${statusBadgeClass(
                run.status
              )}`}
            >
              <Radio className="mr-1 h-3 w-3" />
              {run.status || 'draft'}
            </span>
            <button
              type="button"
              onClick={() => !loading && onClose()}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-4 px-5 py-4 bg-slate-50">
          {/* Left column: run summary */}
          <div className="border-b md:border-b-0 md:border-r border-slate-200 pr-0 md:pr-4 pb-4 md:pb-0">
            <h3 className="text-xs font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3 text-slate-500" />
              Run Overview
            </h3>

            <div className="space-y-2 text-xs text-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Date</span>
                <span className="font-medium">
                  {formatRunDate(run.run_date)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-500">Time Window</span>
                <span className="font-medium flex items-center gap-1">
                  <Clock className="h-3 w-3 text-slate-400" />
                  {run.start_time ? formatTime(run.start_time) : '--:--'} -{' '}
                  {run.end_time ? formatTime(run.end_time) : '--:--'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-500">Upload Mode</span>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    run.upload_mode === 'auto'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}
                >
                  {run.upload_mode === 'auto' ? 'Auto' : 'Manual'}
                </span>
              </div>

              <div className="mt-3">
                <span className="block text-slate-500 mb-1">Title</span>
                <p className="text-xs font-medium text-slate-800">
                  {run.title || 'No title'}
                </p>
              </div>

              <div className="mt-3">
                <span className="block text-slate-500 mb-1">Notes</span>
                <p className="text-xs text-slate-700 whitespace-pre-line bg-white rounded-lg border border-slate-200 p-2 min-h-[56px]">
                  {run.notes || 'No special instructions'}
                </p>
              </div>
            </div>
          </div>

          {/* Right column: properties / items */}
          <div className="pt-4 md:pt-0 md:pl-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <MapPin className="h-3 w-3 text-slate-500" />
                Properties in this run
              </h3>
              <span className="text-[10px] text-slate-500">
                {items.length} stop{items.length === 1 ? '' : 's'}
              </span>
            </div>

            {loading && (
              <div className="flex items-center justify-center py-10 text-xs text-slate-500">
                Loading run details...
              </div>
            )}

            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-xs text-slate-500 border border-dashed border-slate-200 rounded-xl bg-white">
                <Inbox className="h-4 w-4 mb-2 text-slate-400" />
                <p>No properties configured for this run yet.</p>
              </div>
            )}

            {!loading && items.length > 0 && (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {items.map((item, idx) => {
                  const { date: itemDate, time: windowStartTime } =
                    formatDateTime(item.window_start || run.run_date);
                  const { time: windowEndTime } =
                    formatDateTime(item.window_end);

                  return (
                    <div
                      key={item.item_id || idx}
                      className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col gap-1.5 shadow-[0_1px_3px_rgba(15,23,42,0.06)]"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-700">
                            {item.position || idx + 1}
                          </span>
                          <div>
                            <p className="text-[11px] font-semibold text-slate-800">
                              {item.property_name || `Property #${item.property_id || '-'}`}
                            </p>
                            <p className="text-[10px] text-slate-500 flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {item.client_name
                                ? item.client_name
                                : item.client_id
                                ? `Client #${item.client_id}`
                                : 'No client set'}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            item.delivery_mode === 'auto'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {item.delivery_mode === 'auto' ? 'Auto' : 'Manual'}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {item.updated_at} {item.window_start} – {item.window_end}
                        </span>
                      </div>

                      {/* checklist summary if present */}
                      {item.checklist_json && (
                        <div className="mt-1">
                          <p className="text-[10px] font-medium text-slate-600 mb-0.5">
                            Checklist
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              try {
                                const arr = JSON.parse(item.checklist_json);
                                return arr.slice(0, 4).map((c, i) => (
                                  <span
                                    key={c.id || i}
                                    className="inline-flex rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-[10px] text-slate-600"
                                  >
                                    {c.item}
                                  </span>
                                ));
                              } catch {
                                return null;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-slate-200 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RunDetailsModal;
